import React from 'react';
import './AdminSidebar.css';
import { useNavigate } from 'react-router';
import MagicButton from 'components/MagicButton/MagicButton';
import { useDispatch } from 'react-redux';
import { logout } from 'store/services/auth/authSlice';
import { meApi } from 'store/services/user/meApi';
import { haveListApi } from 'store/services/collectionList/haveList/haveListApi';
import { wantListApi } from 'store/services/collectionList/wantList/wantListApi';
import { tradeApi } from 'store/services/trade/tradeApi';
import { cardsSearchApi } from 'store/services/cards/search/cardsSearchApi';
import { authApi } from 'store/services/auth/authApi';
import { adminApi } from 'store/services/admin/adminApi';
import { feedbackApi, useGetAdminFeedbackStatsQuery } from 'store/services/feedback/feedbackApi';
import { caseApi, useGetCaseBadgeCountQuery } from 'store/services/case/caseApi';
import { AssetUrls } from 'config/assets';
import { useUserRoles } from 'hooks/useUserRoles';
import { useSidebarCollapse } from 'context/SidebarCollapseContext';
import ChevronLeftIcon from 'components/Icons/ChevronLeftIcon';

interface Props {
  currentPath: string;
  isMobile?: boolean;
  isMobileMenuOpen?: boolean;
  onMenuClose?: () => void;
}

interface AdminRoute {
  path: string;
  title: string;
  icon: string;
  badge?: number;
  writerVisible?: boolean;
}

const ADMIN_ROUTES: AdminRoute[] = [
  {
    path: '/admin/dashboard',
    title: 'Dashboard',
    icon: AssetUrls.icons.navigation.adminHome
  },
  {
    path: '/admin/controls',
    title: 'Controls',
    icon: AssetUrls.icons.navigation.controls
  },
  {
    path: '/admin/users',
    title: 'Users',
    icon: AssetUrls.icons.navigation.adminUsers
  },
  {
    path: '/admin/cards',
    title: 'Cards',
    icon: AssetUrls.icons.navigation.adminCards
  },
  {
    path: '/admin/cases',
    title: 'Case Resolution',
    icon: AssetUrls.icons.navigation.adminCases
  },
  {
    path: '/admin/trades',
    title: 'Trades',
    icon: AssetUrls.icons.navigation.adminTrades
  },
  {
    path: '/admin/transactions',
    title: 'Transactions',
    icon: AssetUrls.icons.navigation.adminTransactions
  },
  {
    path: '/admin/cronjobs',
    title: 'Cron Jobs',
    icon: AssetUrls.icons.navigation.adminCronJobs
  },
  {
    path: '/admin/drip-campaign',
    title: 'Drip Campaigns',
    icon: AssetUrls.icons.navigation.adminFeedback
  },
  {
    path: '/admin/message',
    title: 'Message',
    icon: AssetUrls.icons.navigation.adminFeedback
  },
  {
    path: '/admin/feedback',
    title: 'Feedback',
    icon: AssetUrls.icons.navigation.adminFeedback
  },
  {
    path: '/admin/seo',
    title: 'SEO Report',
    icon: AssetUrls.icons.navigation.adminHome
  },
  {
    path: '/admin/blog/articles',
    title: 'Blog',
    icon: AssetUrls.icons.navigation.guide,
    writerVisible: true
  }
];

const AdminSidebar = ({ currentPath, isMobile = false, isMobileMenuOpen = false, onMenuClose }: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAdmin } = useUserRoles();
  const { isCollapsed, toggleCollapse } = useSidebarCollapse();
  const effectiveCollapsed = !isMobile && isCollapsed;

  // Filter sidebar routes based on role: writers only see writerVisible routes
  const visibleRoutes = isAdmin
    ? ADMIN_ROUTES
    : ADMIN_ROUTES.filter(route => route.writerVisible);

  // Fetch feedback stats to get count of "new" status feedback (poll every 60 seconds)
  // Only fetch for admins — writers don't have access to feedback endpoints
  const { data: feedbackStats } = useGetAdminFeedbackStatsQuery(undefined, {
    pollingInterval: 60000,
    skip: !isAdmin || !document.hasFocus()
  });

  // Fetch case badge count (poll every 60 seconds)
  // Only fetch for admins — writers don't have access to case endpoints
  const { data: caseBadgeData } = useGetCaseBadgeCountQuery(undefined, {
    pollingInterval: 60000,
    skip: !isAdmin || !document.hasFocus()
  });

  // Get count of "new" status feedback from stats
  const newFeedbackCount = feedbackStats?.byStatus?.new || 0;

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    if (isMobile && onMenuClose) {
      onMenuClose();
    }
  };

  const handleLogout = () => {
    // Clear all RTK Query caches to prevent data leakage between accounts
    dispatch(meApi.util.resetApiState());
    dispatch(haveListApi.util.resetApiState());
    dispatch(wantListApi.util.resetApiState());
    dispatch(tradeApi.util.resetApiState());
    dispatch(cardsSearchApi.util.resetApiState());
    dispatch(authApi.util.resetApiState());
    dispatch(adminApi.util.resetApiState());
    dispatch(feedbackApi.util.resetApiState());
    dispatch(caseApi.util.resetApiState());

    // Clear auth token
    dispatch(logout());

    // Navigate to welcome page
    navigate('/welcome');
  };

  return (
    <div className={`admin-sidebar ${isMobile ? 'mobile' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''} ${effectiveCollapsed ? 'collapsed' : ''}`}>
      {/* Logo Header */}
      <div className="admin-sidebar-header">
        <img
          src={AssetUrls.logos.iconLogo}
          width={30}
          alt="icon-logo"
        />
        <div className="admin-sidebar-header-title">trademagic.gg</div>
        {!isMobile && (
          <button
            className={`sidebar-collapse-toggle ${effectiveCollapsed ? 'collapsed' : ''}`}
            onClick={toggleCollapse}
            aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronLeftIcon size={14} color="#878787" />
          </button>
        )}
      </div>

      {/* Send Cards Button */}
      <div className="sidebar-send-cards-wrapper" data-tooltip="Send Cards">
      <MagicButton
        label="Send Cards"
        icon={
          <img
            src={AssetUrls.logos.arevakhachIcon}
            alt="send-icon"
            style={{ width: 20, height: 20 }}
          />
        }
        color="purple"
        fullWidth
        onPress={() => handleMenuItemClick('/sendcards')}
      />
      </div>

      {/* Admin Navigation Items */}
      <div className="admin-sidebar-items-container">
        {visibleRoutes.map((route) => {
          const isSelected = currentPath === route.path;
          const itemClassName = `admin-sidebar-item ${isSelected ? 'selected' : ''}`;
          const labelClassName = `admin-sidebar-item-label ${isSelected ? 'selected' : ''}`;

          // Add dynamic badges for Feedback and Case Resolution routes
          let badge = route.badge;
          if (route.path === '/admin/feedback' && newFeedbackCount > 0) {
            badge = newFeedbackCount;
          } else if (route.path === '/admin/cases' && caseBadgeData?.unreadCount) {
            badge = caseBadgeData.unreadCount;
          }

          return (
            <div
              key={route.path}
              className={itemClassName}
              data-tooltip={route.title}
              onClick={() => handleMenuItemClick(route.path)}
            >
              <img src={route.icon} alt={`${route.title}-icon`} />
              <div className={labelClassName}>{route.title}</div>
              {badge && (
                <div className="admin-sidebar-badge">{badge}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Home and Logout */}
      <div className="admin-sidebar-footer">
        <div className="admin-sidebar-item" data-tooltip="Home" onClick={() => handleMenuItemClick('/my-cards')}>
          <img
            src={AssetUrls.icons.navigation.adminHome}
            alt="home-icon"
          />
          <div className="admin-sidebar-item-label">Home</div>
        </div>

        <div className="admin-sidebar-item" data-tooltip="Logout" onClick={handleLogout}>
          <img
            src={AssetUrls.icons.dropdowns.arrowRight}
            alt="logout-icon"
          />
          <div className="admin-sidebar-item-label">Logout</div>
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
